import React, { useState } from "react";
import { Send, Paperclip, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "comunicado", label: "Comunicado / Ofício" },
  { key: "solicitacao", label: "Solicitação formal" },
  { key: "documento", label: "Envio de documento" },
  { key: "outro", label: "Outro" },
];

const SENDER_TYPES = [
  { key: "cooperado", label: "Sou cooperado(a)" },
  { key: "instituicao", label: "Sou de uma instituição" },
  { key: "outro", label: "Outro" },
];

export default function SecretariaEnviar() {
  const [form, setForm] = useState({
    sender_name: "",
    sender_email: "",
    sender_phone: "",
    sender_type: "cooperado",
    institution_name: "",
    subject: "",
    category: "comunicado",
    body: "",
  });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      await api.post("/secretariat/messages", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Mensagem enviada! A secretaria responderá em breve.");
      setSuccess(true);
      setForm({ sender_name: "", sender_email: "", sender_phone: "", sender_type: "cooperado", institution_name: "", subject: "", category: "comunicado", body: "" });
      setFile(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (success) {
    return (
      <div data-testid="secretaria-success" className="py-24">
        <div className="container-page max-w-2xl text-center">
          <CheckCircle2 className="w-16 h-16 text-forest-700 mx-auto mb-6" />
          <h1 className="font-display text-4xl text-forest-900">Mensagem enviada!</h1>
          <p className="text-forest-900/80 mt-4 leading-relaxed">
            A equipe da secretaria recebeu sua mensagem e responderá em breve no e-mail informado. Obrigado por nos
            procurar.
          </p>
          <button onClick={() => setSuccess(false)} data-testid="send-another" className="btn-outline mt-8">
            Enviar outra mensagem
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="secretaria-enviar-page" className="py-20">
      <div className="container-page grid lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-5">
          <div className="eyebrow mb-3">Secretaria Virtual</div>
          <h1 className="font-display text-4xl md:text-5xl text-forest-900 leading-tight">
            Envie uma mensagem para a secretaria da cooperativa.
          </h1>
          <div className="section-divider my-7" />
          <p className="text-forest-900/80 leading-relaxed">
            Use este canal para enviar comunicados, ofícios, solicitações formais (como segunda via de documentos,
            declarações de cooperado) ou anexar documentação que precise ser registrada.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-forest-900/85">
            <li>• Anexos até 10 MB nos formatos PDF, DOC, DOCX, JPG, PNG, WEBP</li>
            <li>• Resposta no e-mail informado, em até 5 dias úteis</li>
            <li>• Suas informações são privadas e usadas só para esse atendimento</li>
          </ul>
        </aside>

        <form onSubmit={submit} className="lg:col-span-7 bg-white border border-cream-400 p-8 md:p-10 space-y-5" data-testid="secretaria-form">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Seu nome completo" name="sender_name" value={form.sender_name} onChange={onChange} required />
            <Field label="E-mail" name="sender_email" type="email" value={form.sender_email} onChange={onChange} required />
            <Field label="Telefone (opcional)" name="sender_phone" value={form.sender_phone} onChange={onChange} />
            <div>
              <Label>Você está enviando como</Label>
              <Select value={form.sender_type} onValueChange={(v) => setForm({ ...form, sender_type: v })}>
                <SelectTrigger data-testid="sender-type-select" className="rounded-none border-cream-400 bg-cream-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SENDER_TYPES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.sender_type === "instituicao" && (
            <Field label="Nome da instituição" name="institution_name" value={form.institution_name} onChange={onChange} required />
          )}

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Assunto" name="subject" value={form.subject} onChange={onChange} required />
            <div>
              <Label>Tipo de mensagem</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="category-select" className="rounded-none border-cream-400 bg-cream-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Mensagem</Label>
            <textarea
              name="body"
              required
              rows={7}
              value={form.body}
              onChange={onChange}
              data-testid="body-textarea"
              className="mt-2 w-full bg-cream-100 border border-cream-400 focus:border-forest-700 focus:outline-none p-4 text-forest-900 resize-none"
            />
          </div>

          <div>
            <Label>Anexar arquivo (opcional)</Label>
            <label className="mt-2 flex items-center gap-3 px-4 py-3 border border-dashed border-cream-400 bg-cream-100 cursor-pointer hover:bg-cream-200">
              <Paperclip className="w-4 h-4 text-forest-700" />
              <span className="text-sm text-forest-900/80">
                {file ? file.name : "Selecionar arquivo (PDF, DOC, JPG, PNG — até 10 MB)"}
              </span>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="file-input"
                className="hidden"
              />
            </label>
          </div>

          <button type="submit" disabled={sending} data-testid="submit-button" className="btn-primary">
            {sending ? "Enviando..." : (<>Enviar para a secretaria <Send className="w-4 h-4" /></>)}
          </button>
        </form>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <span className="block text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">{children}</span>;
}

function Field({ label, name, value, onChange, type = "text", required = false }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <input
        type={type}
        name={name}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={`field-${name}`}
        className="mt-2 w-full bg-cream-100 border border-cream-400 focus:border-forest-700 focus:outline-none p-3.5 text-forest-900"
      />
    </label>
  );
}
